import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  MessageCircleIcon,
} from 'lucide-react';
import { useState } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { useListWhatsAppAccounts } from '@/features/integrations/api';
import { WhatsAppConnectButton } from '@/features/integrations/components/whatsapp-connect-button';
import { authRedirectSearchValue } from '@/lib/auth-redirect';
import { ensureSession } from '@/lib/session';
import { ConnectRouteError } from './-connect-route-error';

/**
 * Browser hand-off target for connecting WhatsApp from the mobile app.
 *
 * Meta's Embedded Signup runs through the Facebook JS SDK popup, which only
 * works from a domain on the Meta app's JS-SDK allowlist. The Capacitor
 * WebView's origin (`https://localhost`) can never be on it, so the native
 * "Connect" button opens the system browser here instead. This page runs the
 * popup from the real app domain; once done the user closes the browser and
 * the app refreshes its connection state on resume.
 *
 * Deliberately outside `_authed` so it renders as a focused, chrome-free page
 * — but it still requires a signed-in user who belongs to an organization,
 * because `/integrations/whatsapp/finalize` is org-scoped.
 */
export const Route = createFileRoute('/connect/whatsapp')({
  beforeLoad: async ({ location }) => {
    const session = await ensureSession();

    if (session.unavailable) {
      throw new Error('Could not load your session');
    }

    if (!session.user) {
      throw redirect({
        to: '/sign-in',
        search: { redirect: authRedirectSearchValue(location) },
      });
    }

    if (!session.user.organizationId) {
      throw redirect({ to: '/onboarding' });
    }
  },
  errorComponent: ConnectRouteError,
  component: ConnectWhatsAppPage,
});

function ConnectWhatsAppPage() {
  const [justConnected, setJustConnected] = useState(false);
  // The connect went through but Meta will not let the number send: the
  // server says why and what to do, and it has to stay on screen to be read.
  const [connectProblem, setConnectProblem] = useState<string | null>(null);
  const { accounts } = useListWhatsAppAccounts();

  return (
    <>
      <title>Connect WhatsApp | Borrah</title>
      <div className="mx-auto flex min-h-svh max-w-lg items-center px-4">
        <Card className="w-full">
          <CardContent className="space-y-4 pt-6">
            {justConnected ? (
              <>
                <div className="flex items-center gap-2">
                  <CheckCircle2Icon className="text-primary size-5" />
                  <h1 className="text-lg font-semibold">
                    WhatsApp is connected
                  </h1>
                </div>
                <p className="text-muted-foreground text-sm">
                  You&rsquo;re all set. Close this window to return to the
                  Borrah app — your WhatsApp connection will show up there
                  automatically.
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <MessageCircleIcon className="text-primary size-5" />
                  <h1 className="text-lg font-semibold">
                    Connect WhatsApp Business
                  </h1>
                </div>
                <p className="text-muted-foreground text-sm">
                  Continue with Facebook to link your WhatsApp Business number.
                  A Facebook window will open — sign in if asked, pick your
                  business, and follow the steps.
                </p>
                {accounts.length > 0 && (
                  <p className="text-muted-foreground rounded-lg border p-3 text-xs">
                    Already connected:{' '}
                    {accounts.map((a) => a.phoneNumber).join(', ')}. Continuing
                    will connect another number.
                  </p>
                )}
                {connectProblem && (
                  <Alert variant="destructive">
                    <AlertTriangleIcon />
                    <AlertTitle>
                      WhatsApp can&rsquo;t send messages yet
                    </AlertTitle>
                    <AlertDescription>{connectProblem}</AlertDescription>
                  </Alert>
                )}
                <WhatsAppConnectButton
                  className="w-full"
                  label={
                    connectProblem ? 'Try again' : 'Continue with Facebook'
                  }
                  onSuccess={() => {
                    setConnectProblem(null);
                    setJustConnected(true);
                  }}
                  onError={(error) => setConnectProblem(error.message || null)}
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
